import React, { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/ui/button';

// Custom pen cursor as SVG data URI
const penCursorSVG = `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M12 19l7-7 3 3-7 7-3-3z"/>
  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
  <path d="M2 2l7.586 7.586"/>
  <circle cx="11" cy="11" r="2"/>
</svg>
`)}`;

// Alternative simpler pen cursor
const simplePenCursor = `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
  <path fill="black" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
</svg>
`)}`;

const SignatureCanvas = forwardRef(({ onSignatureChange, onClear, initialSignature, heightClass }, ref) => {
  const signatureRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(!!initialSignature);
  const signatureDataRef = useRef(initialSignature || null);
  const lastResizeTime = useRef(0);
  const restoreScheduled = useRef(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasWrapperRef = useRef(null);

  // Expose restore method to parent components
  useImperativeHandle(ref, () => ({
    restoreSignature: () => {
      if (signatureDataRef.current && signatureRef.current) {
        signatureRef.current.fromDataURL(signatureDataRef.current);
        setHasSignature(true);
        return true;
      }
      return false;
    },
    getSignatureData: () => signatureDataRef.current,
    hasSignatureData: () => !!signatureDataRef.current
  }));

  // Restore signature data to canvas
  const restoreSignatureData = useCallback(() => {
    if (signatureDataRef.current && signatureRef.current) {
      try {
        signatureRef.current.fromDataURL(signatureDataRef.current);
        setHasSignature(true);
        restoreScheduled.current = false;
      } catch (e) {
        console.error('Failed to restore signature:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Load initial signature if provided
    if (initialSignature && signatureRef.current) {
      signatureRef.current.fromDataURL(initialSignature);
      signatureDataRef.current = initialSignature;
      setHasSignature(true);
    }
  }, [initialSignature]);

  // Handle resize events with debouncing
  useEffect(() => {
    let resizeTimeout;
    
    const handleResize = () => {
      const now = Date.now();
      
      // Debounce: Only process if at least 500ms since last resize
      if (now - lastResizeTime.current < 500) {
        // Reset the timeout to restore later
        if (resizeTimeout) clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          restoreSignatureData();
        }, 500);
        return;
      }
      
      lastResizeTime.current = now;
      
      // Clear any pending restore
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      
      // Schedule restore
      if (!restoreScheduled.current && signatureDataRef.current) {
        restoreScheduled.current = true;
        resizeTimeout = setTimeout(() => {
          restoreSignatureData();
        }, 500);
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Visual viewport API for mobile zoom detection
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [restoreSignatureData]);

  // Prevent scrolling during signature drawing (especially for DuckDuckGo mobile)
  // Option 2: Body Scroll Lock approach
  useEffect(() => {
    const canvasWrapper = canvasWrapperRef.current;
    if (!canvasWrapper) return;

    // Store original body styles to restore later
    let originalOverflow = '';
    let originalPosition = '';
    let originalTop = '';
    let scrollPosition = 0;

    // Lock body scroll
    const lockBodyScroll = () => {
      // Save current scroll position
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      
      // Save original styles
      originalOverflow = document.body.style.overflow;
      originalPosition = document.body.style.position;
      originalTop = document.body.style.top;
      
      // Lock the body
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = '100%';
    };

    // Unlock body scroll
    const unlockBodyScroll = () => {
      // Restore original styles
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = '';
      
      // Restore scroll position
      window.scrollTo(0, scrollPosition);
    };

    // Prevent scroll when touching/drawing on canvas
    const preventScroll = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Handle touch start - lock body and mark as drawing
    const handleTouchStart = (e) => {
      setIsDrawing(true);
      lockBodyScroll();
    };

    // Handle touch end - unlock body and mark as not drawing
    const handleTouchEnd = () => {
      setIsDrawing(false);
      unlockBodyScroll();
    };

    // Add event listeners with passive: false to allow preventDefault
    canvasWrapper.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvasWrapper.addEventListener('touchmove', preventScroll, { passive: false });
    canvasWrapper.addEventListener('touchend', handleTouchEnd, { passive: false });
    canvasWrapper.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      // Clean up and ensure body is unlocked
      unlockBodyScroll();
      canvasWrapper.removeEventListener('touchstart', handleTouchStart);
      canvasWrapper.removeEventListener('touchmove', preventScroll);
      canvasWrapper.removeEventListener('touchend', handleTouchEnd);
      canvasWrapper.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isDrawing]);

  const handleEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setHasSignature(true);
      const signatureData = signatureRef.current.toDataURL('image/png');
      signatureDataRef.current = signatureData;
      onSignatureChange(signatureData);
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    signatureDataRef.current = null;
    setHasSignature(false);
    onClear();
  };

  // Determine height based on heightClass prop
  const getCanvasHeight = () => {
    if (heightClass?.includes('h-48')) return 192;
    if (heightClass?.includes('h-40')) return 160;
    if (heightClass?.includes('h-32')) return 128;
    return 150;
  };

  // Custom cursor style for pen
  const canvasCursorStyle = {
    width: '100%', 
    height: getCanvasHeight(),
    touchAction: 'none',
    cursor: `url('${simplePenCursor}') 2 20, crosshair`
  };

  return (
    <div className="space-y-3">
      <div 
        ref={canvasWrapperRef}
        className="relative border-2 border-dashed border-gray-300 rounded-lg bg-white hover:border-tca-blue transition-colors overflow-hidden"
      >
        <ReactSignatureCanvas
          ref={signatureRef}
          penColor="#000000"
          minWidth={1.5}
          maxWidth={3}
          velocityFilterWeight={0.7}
          canvasProps={{
            className: `w-full ${heightClass || 'h-32 sm:h-40'}`,
            style: canvasCursorStyle
          }}
          onEnd={handleEnd}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-gray-400 text-sm">Draw your signature here</p>
          </div>
        )}
      </div>
      <div className="flex justify-between items-center" data-hide-in-pdf="true">
        <p className="text-xs text-gray-500">Use your mouse or finger to sign above</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearSignature}
          className="text-destructive hover:text-destructive"
        >
          Clear Signature
        </Button>
      </div>
    </div>
  );
});

SignatureCanvas.displayName = 'SignatureCanvas';

export { SignatureCanvas };
export default SignatureCanvas;
